import { useState } from "react";
import { useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import type { UpdateAttemptPayload } from "@/react/api/quizAttemptsApi";
import {
  ModalManager,
  useModal,
} from "@/react/components/Layout/ModalManagerProvider";
import { Button } from "@circle-react-uikit/Button";
import { Form } from "@circle-react-uikit/Form";
import { Loader } from "@circle-react-uikit/Loader";
import { Modal } from "@circle-react-uikit/ModalV2";
import { MODAL_SIZES } from "@circle-react-uikit/ModalV2/ModalContent";
import { useToast } from "@circle-react-uikit/ToastV2";
import { Typography } from "@circle-react-uikit/Typography";
import type { QuizCommunityMember } from "../../QuizResults";
import { Content } from "./Content";
import { useGetQuizAttemptForAdmin } from "./hooks/useGetQuizAttemptForAdmin";
import { useUpdateQuizAttempt } from "./hooks/useUpdateQuizAttempt";

interface QuizResultModalProps {
  attemptId: number;
  quizId: number;
  refetchAttempts: () => void;
  student: QuizCommunityMember;
}
const QuizResultModalComponent = ({
  quizId,
  attemptId,
  student,
  refetchAttempts,
}: QuizResultModalProps) => {
  const modal = useModal();
  const { error } = useToast();
  const queryClient = useQueryClient();
  const translationRoot = "courses.admin_summary.engagement.quiz_results";
  const { isLoading, data } = useGetQuizAttemptForAdmin({ quizId, attemptId });
  const hasPassingGrade =
    data?.passing_grade !== null && data?.passing_grade !== undefined;
  const actionLabel = t([
    translationRoot,
    hasPassingGrade ? "actions.quiz_result" : "actions.grade_quiz",
  ]);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState<boolean>(false);
  const { mutate, isSaving } = useUpdateQuizAttempt({
    quizId,
    attemptId,
    onSuccess: () => {
      refetchAttempts();
      void queryClient.invalidateQueries(["attempt", quizId, attemptId, true]);
      void modal.hide();
    },
  });

  if (!modal || !modal.visible) return null;
  if (!isLoading && !data) {
    error(t([translationRoot, "fetch_error"]));
    return null;
  }
  return (
    <Modal
      isOpen={modal.visible}
      contentPortalId={`quiz-result-${attemptId}-modal-portal`}
    >
      <Modal.Overlay onClick={modal.hide} />
      <Modal.Content size={MODAL_SIZES["2xl"]}>
        <Modal.Header className="z-10 md:!py-4">
          <Typography.TitleMd>{actionLabel}</Typography.TitleMd>
          <Modal.HeaderActions>
            <Modal.CloseButton onClick={modal.hide} />
          </Modal.HeaderActions>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <Loader />
          ) : (
            <Form
              id={`update-quiz-attempt-${data.id}`}
              onSubmit={(payload: UpdateAttemptPayload) => {
                mutate(payload);
              }}
              defaultValues={{ result: data.result }}
            >
              <Content
                attempt={data}
                student={student}
                setIsSubmitEnabled={setIsSubmitEnabled}
              />
            </Form>
          )}
        </Modal.Body>
        {!isLoading && !hasPassingGrade && (
          <Modal.Footer className="border-t-primary border-t py-4">
            <div className="flex justify-end gap-4">
              <Button
                large
                type="button"
                variant="secondary"
                onClick={modal.hide}
              >
                {t("cancel")}
              </Button>
              <Button
                large
                type="submit"
                variant="circle"
                form={`update-quiz-attempt-${data.id}`}
                disabled={!isSubmitEnabled || isSaving}
              >
                {actionLabel}
              </Button>
            </div>
          </Modal.Footer>
        )}
      </Modal.Content>
    </Modal>
  );
};

const QuizResultModal = ModalManager.create((props: QuizResultModalProps) => (
  <QuizResultModalComponent {...props} />
));
export const useQuizResultModal = () => useModal(QuizResultModal);
